import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, guardedClient as request, constructQuery } from '@repo/shared/helpers';
import SubcontractingTab from 'src/parts/tabs/SubcontractingTab';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getEvent, getEventsWithHierarchy } from 'src/api/admin/events';
import { getEventTypes } from 'src/api/admin/types';
import { getAllTags } from 'src/api/admin/tags';
interface SubcontractingSearch {
	page?: number;
	sort?: string;
}

export const Route = createFileRoute('/_admin/events/$eventId/subcontracting')({
	validateSearch: (search: Record<string, unknown>): SubcontractingSearch => ({
		page: search.page as number | undefined,
		sort: search.sort as string | undefined,
	}),
	component: SubcontractingPage,
});

function SubcontractingPage() {
	const { eventId } = Route.useParams();
	const { page, sort } = Route.useSearch();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { config, loading: configLoading } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const usesTags = featureFlags?.USES_TAGS || false;

	const { data: parentEvent, isLoading: eventLoading } = useQuery({
		queryKey: ['event', eventId],
		queryFn: () => getEvent(eventId),
	});

	const projectQuery = constructQuery({
		page,
		sort,
		projectIds: parentEvent?.projectId,
		parentEventId: eventId,
	});

	const { data: eventsData, isLoading: eventsLoading } = useQuery({
		queryKey: ['events-hierarchy', eventId, page, sort],
		queryFn: () => getEventsWithHierarchy(projectQuery),
		enabled: !!parentEvent?.projectId,
	});

	const { data: typesData } = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes(),
	});

	const { data: tagsData } = useQuery({
		queryKey: ['tags'],
		queryFn: () => getAllTags(),
		enabled: usesTags,
	});

	const verifyPermission = authenticatedUser ? getVerifyPermission(authenticatedUser.permissions) : () => false;

	const handleReload = () => {
		queryClient.invalidateQueries({ queryKey: ['events-hierarchy', eventId] });
		queryClient.invalidateQueries({ queryKey: ['event', eventId] });
	};

	const addEvent = async (data: unknown) => {
		await request.post(`/api/events/${eventId}/subcontracting`, data);
		handleReload();
	};

	const isLoading = authLoading || configLoading || eventLoading || eventsLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!authenticatedUser || !parentEvent) return null;

	return (
		<SubcontractingTab
			authenticatedUser={authenticatedUser}
			events={eventsData}
			addEvent={addEvent}
			permissions={{
				view: verifyPermission(PERMISSIONS.adminEventsView),
				create: verifyPermission(PERMISSIONS.adminEventsCreate),
				update: verifyPermission(PERMISSIONS.adminEventsEdit),
				archive: verifyPermission(PERMISSIONS.adminEventsArchive),
			}}
			onReload={handleReload}
			types={typesData?.data || []}
			tags={tagsData || []}
			usesTags={usesTags}
			parentEventId={eventId}
			parentEvent={parentEvent}
			project={parentEvent?.project}
			skipOwnerEmailDomainCheck
		/>
	);
}
